/**
 * ingredient-reveal.js — hover the product media, the cursor becomes an "Ingredients"
 * label; click and the media reveals the ingredient list. Click again to go back.
 *
 * Modelled on upfront.nl, whose implementation was inspected directly: a cursor-following
 * pill with two states, over the product media, toggling an ingredients panel.
 *
 * One deliberate difference. Upfront reuses their spin clip, seeking it to the frame where
 * the pack's back faces camera. Our clips have no back view — the jar never rotates — so
 * there is nothing to seek to. This cross-fades the media into a panel instead. The element
 * checks for a `data-back-time` and will seek-and-hold a clip instead of cross-fading if
 * one is ever supplied, so re-rendered clips with a real rotation upgrade this for free.
 *
 * Interaction notes that matter:
 *   - The pill is bound to the media box, NOT to a card root. This only ever runs on a
 *     product page, where no full-card link overlay exists. (On cards that overlay covers
 *     the media and swallows pointer events — see findHoverTarget in spin-media.js.)
 *   - Touch devices get a real, always-visible button instead of a cursor pill: there is no
 *     hover state to drive a pill, and a tap must not be ambiguous.
 *   - Keyboard users get the same button, so the panel is never mouse-only.
 *
 * Debug: ?ingredients=debug
 */

(() => {
  const DEBUG = new URLSearchParams(window.location.search).get('ingredients') === 'debug';
  const log = (...a) => { if (DEBUG) console.log('[ingredient-reveal]', ...a); };

  const canHover = () => window.matchMedia('(hover: hover)').matches;

  /**
   * Breathing room kept between the pill and the far edges of the media box, matching the
   * offset the CSS applies so the pill never sits under the cursor. Clamping against these
   * is what stops `overflow: hidden` from eating the label near the right or bottom edge.
   */
  const PILL_MARGIN_X = 12;
  const PILL_MARGIN_Y = 8;

  class IngredientReveal extends HTMLElement {
    /** Instance counter, for unique panel ids that aria-controls can point at. */
    static seq = 0;

    constructor() {
      super();
      this.open = false;
      this.mediaBox = null;
      this.pill = null;
      this.panel = null;
      this.button = null;
      this._onMove = null;
      this._onEnter = null;
      this._onClick = null;
      this._onTouch = null;
      this._onKey = null;
      this._invalidateRect = null;
      this._spinUnfinished = false;
    }

    get labelOpen() { return this.dataset.labelOpen || 'Ingredients'; }
    get labelClose() { return this.dataset.labelClose || 'Back'; }

    /**
     * The media box to overlay. Mirrors spin-media.js so both features attach to the same
     * container and cannot disagree about what "the hero" is.
     */
    findMediaBox() {
      const scope = this.closest('.product__media-item') || this.parentElement;
      if (!scope) return null;
      const img = scope.querySelector('.product-media-container img, .media img, img');
      return img?.closest('.media') || null;
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-ingredient-panel]');
      if (!this.panel) return log('no panel content, nothing to reveal');

      this.mediaBox = this.findMediaBox();
      if (!this.mediaBox) return log('no .media container found to attach to');

      // A re-connect reuses the same instance, so start from a known-closed state rather
      // than whatever the previous mount left behind.
      this.open = false;
      this._spinUnfinished = false;

      // The panel lives inside the media box so it inherits the same geometry the image
      // has, and so `overflow: hidden` clips it identically.
      this.mediaBox.appendChild(this.panel);
      this.panel.hidden = false;
      this.mediaBox.classList.add('has-ingredient-reveal');

      this.buildTrigger();
      this.setAttribute('data-ingredient-state', 'closed');
      log('ready');
    }

    disconnectedCallback() {
      if (this._onMove) this.mediaBox?.removeEventListener('pointermove', this._onMove);
      if (this._onEnter) this.mediaBox?.removeEventListener('pointerenter', this._onEnter);
      if (this._onClick) this.mediaBox?.removeEventListener('click', this._onClick);
      if (this._onTouch) this.mediaBox?.removeEventListener('pointerdown', this._onTouch);
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._invalidateRect) {
        window.removeEventListener('scroll', this._invalidateRect);
        window.removeEventListener('resize', this._invalidateRect);
      }
      this._onMove = null;
      this._onEnter = null;
      this._onClick = null;
      this._onTouch = null;
      this._onKey = null;
      this._invalidateRect = null;

      // connectedCallback MOVES the panel out of this element and appends the button and pill
      // into .media, so they outlive this element unless removed explicitly. Left behind they
      // are orphans: the button keeps a listener bound to a detached instance and silently
      // no-ops for keyboard users, and `ingredient-reveal--cursor` would leave the hero with
      // `cursor: none` and no pill to stand in for it.
      //
      // The panel is TAKEN BACK rather than removed. It is the content, so destroying it
      // means the next connect finds no [data-ingredient-panel] and bails silently — the
      // feature simply gone for the rest of the session after any DOM move that re-parents
      // this element. Re-adopting it leaves the element exactly as it started.
      if (this.panel) {
        this.panel.hidden = true;
        this.appendChild(this.panel);
      }
      this.button?.remove();
      this.pill?.remove();
      this.button = null;
      this.pill = null;
      this.open = false;
      this._spinUnfinished = false;
      this.mediaBox?.classList.remove(
        'has-ingredient-reveal',
        'ingredient-reveal--cursor',
        'is-ingredients-open',
      );
      this.setAttribute('data-ingredient-state', 'closed');
    }

    buildTrigger() {
      // A real button, always. It is the accessible control and the only control on touch.
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ingredient-reveal__button';
      button.setAttribute('aria-expanded', 'false');
      // Name what it controls. Ids are per-instance so several heroes on one page cannot collide.
      if (!this.panel.id) {
        IngredientReveal.seq += 1;
        this.panel.id = `ingredient-panel-${IngredientReveal.seq}`;
      }
      button.setAttribute('aria-controls', this.panel.id);
      button.textContent = this.labelOpen;
      this.mediaBox.appendChild(button);
      this.button = button;
      button.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.toggle(); });

      if (canHover()) {
        // Cursor-following pill. Purely decorative: the button above is the real control,
        // so this is hidden from assistive tech.
        const pill = document.createElement('span');
        pill.className = 'ingredient-reveal__pill';
        pill.setAttribute('aria-hidden', 'true');
        pill.textContent = this.labelOpen;
        this.mediaBox.appendChild(pill);
        this.pill = pill;
        this.mediaBox.classList.add('ingredient-reveal--cursor');

        /*
         * The rect is cached instead of read per event: getBoundingClientRect() forces a
         * layout, and a pointer crossing a hero fires pointermove on the order of a
         * hundred times a second. Scroll and resize are the only things that can move the
         * box, so they are the only things that need to invalidate it.
         */
        let rect = null;
        let pillW = 0;
        let pillH = 0;
        // The pill's own size is cached with the rect: reading offsetWidth is itself a
        // layout read, so measuring it per move would defeat the point. Its size only
        // changes when the label does, and toggle() invalidates for exactly that.
        this._invalidateRect = () => { rect = null; };
        window.addEventListener('scroll', this._invalidateRect, { passive: true });
        window.addEventListener('resize', this._invalidateRect, { passive: true });

        const place = (event) => {
          if (!rect) {
            rect = this.mediaBox.getBoundingClientRect();
            pillW = pill.offsetWidth;
            pillH = pill.offsetHeight;
          }
          // `.media` is overflow: hidden, so an unclamped pill is clipped or entirely
          // invisible near the right and bottom edges.
          const maxX = Math.max(0, rect.width - pillW - PILL_MARGIN_X);
          const maxY = Math.max(0, rect.height - pillH - PILL_MARGIN_Y);
          const x = Math.min(Math.max(event.clientX - rect.left, 0), maxX);
          const y = Math.min(Math.max(event.clientY - rect.top, 0), maxY);
          pill.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          // Only allow it to show once it has a real position. The CSS reveals it on
          // :hover alone, so without this it flashed in the top-left corner every time the
          // pointer entered anywhere other than that corner.
          pill.classList.add('is-placed');
        };

        this._onMove = place;
        this._onEnter = place;
        this.mediaBox.addEventListener('pointermove', place);
        // Position on entry as well as on movement, so the very first frame is correct.
        this.mediaBox.addEventListener('pointerenter', place);

        // Clicking anywhere on the media toggles, which is what makes it feel like the
        // cursor itself is the control.
        this._onClick = (event) => {
          if (event.target.closest('a, button, [role="button"]')) return;
          // Same intent as the button's own handler, so it stops here too. Without this
          // the click ALSO reached spin-media's replay listener on the ancestor slide and
          // restarted the clip — on the one gesture whose whole purpose is to hide it.
          event.stopPropagation();
          this.toggle();
        };
        this.mediaBox.addEventListener('click', this._onClick);
      }

      /*
       * Hybrid devices — a touchscreen laptop, an iPad with a trackpad — report
       * `hover: hover`, so they got the cursor pill AND the visually hidden button, which
       * left a touch user with no visible control at all. The first genuine touch drops
       * cursor mode, which un-hides the real button. The pill is left in place; the CSS
       * only ever shows it while cursor mode is on.
       */
      this._onTouch = (event) => {
        if (event.pointerType !== 'touch') return;
        this.mediaBox.classList.remove('ingredient-reveal--cursor');
        this.mediaBox.removeEventListener('pointerdown', this._onTouch);
        this._onTouch = null;
        log('touch input seen — switching from the cursor pill to the button');
      };
      this.mediaBox.addEventListener('pointerdown', this._onTouch);

      this._onKey = (event) => { if (event.key === 'Escape' && this.open) this.toggle(); };
      document.addEventListener('keydown', this._onKey);
    }

    toggle() {
      // The button is appended into `.media` and outlives a bailed-out re-connect, so it
      // can still be clicked while `this.panel`/`this.mediaBox` are null — which threw
      // "Cannot read properties of null" on the first click and killed the handler.
      if (!this.panel || !this.mediaBox) return log('toggle ignored: not wired up');

      this.open = !this.open;
      const label = this.open ? this.labelClose : this.labelOpen;
      if (this.pill) {
        this.pill.textContent = label;
        // The label width just changed, so the cached pill size used for edge clamping is
        // stale.
        this._invalidateRect?.();
      }
      if (this.button) {
        this.button.textContent = label;
        this.button.setAttribute('aria-expanded', String(this.open));
      }
      this.mediaBox.classList.toggle('is-ingredients-open', this.open);
      this.setAttribute('data-ingredient-state', this.open ? 'open' : 'closed');

      // Scoped to OUR video: an unscoped `video` selector also matched a native Shopify
      // product video sharing the same media box, and paused or seeked that instead.
      const video = this.mediaBox.querySelector('.spin-media__video');

      // Forward-compatibility: if a clip with a real back view is ever supplied, hold it on
      // that frame instead of cross-fading. Nothing sets this today.
      const backTime = parseFloat(this.dataset.backTime || '');
      if (video && Number.isFinite(backTime)) {
        try {
          video.pause();
          video.currentTime = this.open ? backTime : 0;
        } catch { /* seeking can throw before metadata; the cross-fade still applies */ }
      } else if (video) {
        // Opening drops a scrim over the product. A clip left running behind it is
        // consumed invisibly at 6% opacity, and the resting frame the whole illusion
        // depends on is spent for nothing. So it pauses on open, and an unfinished cycle
        // restarts on close. A cycle that already finished is left alone: it is sitting on
        // the frame that matches the still, which is exactly the right resting state.
        if (this.open) {
          this._spinUnfinished = !video.ended;
          video.pause();
        } else if (this._spinUnfinished) {
          this._spinUnfinished = false;
          try {
            video.currentTime = 0;
          } catch { /* seeking can throw before metadata */ }
          video.play().catch(() => { /* refused: the still remains, which is correct */ });
        }
      }

      // Reset the scroll of the element that actually scrolls (__inner, not the panel).
      // Deliberately not gated on reduced motion — this is not an animation.
      if (this.open) {
        const inner = this.panel.querySelector('.ingredient-reveal__inner');
        if (inner) inner.scrollTop = 0;
      }
      log(this.open ? 'opened' : 'closed');
    }
  }

  if (!customElements.get('ingredient-reveal')) {
    customElements.define('ingredient-reveal', IngredientReveal);
  }
})();
